import { createDepositInvoiceThunk } from '@spike/invoices-action';
import { confirmDepositThunk } from '@spike/payments-action';
import {
    createBookingForDepositThunk,
    createBookingThunk as saveBookingThunk
} from 'actions/bookings/BookingsActions';
import { applyPendingPromocodeThunk } from 'actions/promocodes/OnlineBookingPromocodeActions';
import { createWrapper } from 'api/ApiClient';
import moment, { Moment } from 'moment-timezone';
import { ThunkAction } from 'redux-thunk';
import { RootState } from 'store';
import { RootAction } from 'types/Actions';
import { SaveAppointment } from 'types/Appointment';
import { Booking, BookingChannel, BookingStatus, SaveBooking } from 'types/Booking';
import { MarketplaceScheduleType } from 'types/Marketplace';
import { OrderizableType, SaveOrder } from 'types/Order';
import { FeedingTask, MedicationTask } from 'types/Task';
import { v4 as uuid } from 'uuid';
import {
    ONLINE_BOOKING_CONFIRM_BOOKING_DEPOSIT_START,
    ONLINE_BOOKING_CONFIRM_BOOKING_DEPOSIT_SUCCESS,
    ONLINE_BOOKING_CREATE_BOOKING_START,
    ONLINE_BOOKING_CREATE_BOOKING_SUCCESS,
    ONLINE_BOOKING_ERROR
} from 'actions/onlineBooking/OnlineBookingActionsTypes';
import {
    isOnlineBookingBoardingItem,
    isOnlineBookingDaycareItem,
    isOnlineBookingEvaluationItem,
    isOnlineBookingGroomingItem,
    isOnlineBookingSessionItem,
    MedicationScheduleMode,
    OnlineBookingItem
} from 'types/OnlineBooking';
import {
    getOnlineBookingDepositRequired,
    getOnlineBookingItemPrice,
    getOnlineBookingTotalDeposit,
    getOnlineBookingWalkingAddressId
} from 'reducers/onlineBooking/OnlineBookingSelectors';
import {
    getBusinessArea,
    OlbPurchaseItem,
    trackBookingCompleted,
    trackBookingRequested
} from 'utils/analytics';
import { buildWalkingSaveAppointment } from 'actions/onlineBooking/actions/walkingSaveAppointment';
import { resolveWalkingCustomerAddressId, resolveWalkingServiceConfig } from 'types/Walking';
import {
    getBoardingAddonOccurrences,
    getDaycareAddonOccurrences,
    getOnlineBookingAddonUnits
} from 'utils/onlineBookingAddons';
import {
    bookingRequiresApproval,
    getServiceDurationByPet,
    getServicePriceByPet
} from 'utils/serviceUtils';

const toPurchaseItems = (bookingItems: Array<OnlineBookingItem>): Array<OlbPurchaseItem> =>
    bookingItems.flatMap(item =>
        item.service
            ? [
                  {
                      item_id: item.service.id ?? 0,
                      item_name: item.service.name,
                      item_variant: item.pet.name ?? '',
                      item_category: item.service.businessArea,
                      price: getServicePriceByPet(item.service, item.pet),
                      quantity: 1 as const
                  }
              ]
            : []
    );

/**
 * The deposit actually associated with this booking, for analytics.
 *
 * Under per-category rules the marketplace-level `deposit.value` no longer
 * describes what a booking was charged — in `specific` applicability it is
 * routinely 0 while the real amounts live on the per-category rules. Prefer the
 * server's invoice total, falling back to the client-side calculation.
 */
const getBookingDepositValue = (state: RootState): number => {
    const invoiceDepositTotal = Number(state.invoices.depositInvoice?.deposits?.total);

    if (Number.isFinite(invoiceDepositTotal) && invoiceDepositTotal > 0) {
        return invoiceDepositTotal;
    }

    return getOnlineBookingTotalDeposit(state);
};

export const createBookingThunk = (): ThunkAction<
    Promise<Booking>,
    RootState,
    null,
    RootAction
> => {
    return async (dispatch, getState) => {
        const existingBooking = getState().bookings.booking;

        if (existingBooking?.id) {
            try {
                dispatch({
                    type: ONLINE_BOOKING_CREATE_BOOKING_START
                });

                const state = getState();

                if (
                    getOnlineBookingDepositRequired(state) &&
                    !state.invoices.depositInvoice?.uuid
                ) {
                    await dispatch(
                        createDepositInvoiceThunk(createWrapper(state), existingBooking.id)
                    );
                }

                await dispatch(applyPendingPromocodeThunk());
                dispatch({
                    type: ONLINE_BOOKING_CREATE_BOOKING_SUCCESS
                });
                return existingBooking;
            } catch (error) {
                dispatch({ type: ONLINE_BOOKING_ERROR });
                throw error;
            }
        }

        try {
            dispatch({
                type: ONLINE_BOOKING_CREATE_BOOKING_START
            });

            const state = getState();
            const {
                id: marketplaceId,
                slug,
                scheduleType,
                address,
                timeZone,
                onlineBookingPreferences
            } = state.marketplace.data;
            const { data: masterData } = state.masterData;
            const { notes, customer, bookingItems } = state.onlineBooking;

            const addressId = address?.id;
            const customerId = customer?.id;

            if (!customerId) {
                throw new Error('Customer not found');
            }

            if (!addressId) {
                throw new Error('Marketplace address not found');
            }

            const today = moment().tz(timeZone);

            let orderData: SaveOrder | undefined = undefined;

            if (bookingItems.some(item => item.addons.length > 0)) {
                orderData = {
                    uuid: uuid(),
                    marketplaceId,
                    customerId: customerId,
                    issuedAt: today,
                    items: bookingItems.flatMap(item => {
                        const buildAddonOrderItem = (
                            addon: (typeof item.addons)[number],
                            startedAt?: Moment,
                            quantity = 1
                        ) => ({
                            uuid: uuid(),
                            quantity,
                            discount: 0,
                            staffId: 'staff' in item && item.staff ? item.staff.id : undefined,
                            amount: addon.price,
                            orderableId: addon.id,
                            orderableType: OrderizableType.AddonService,
                            petId: Number(item.pet.id),
                            description: addon.name,
                            marketplaceId,
                            startedAt,
                            orderizableId: addon.id,
                            orderizableType: OrderizableType.AddonService
                        });

                        if (isOnlineBookingDaycareItem(item)) {
                            return item.addons.flatMap(addon =>
                                getDaycareAddonOccurrences(item, addon).map(occurrence => {
                                    const addonDate = occurrence.date.clone();

                                    if (item.dropOffTime) {
                                        addonDate
                                            .set('hour', item.dropOffTime.hour)
                                            .set('minute', item.dropOffTime.minute);
                                    }

                                    return buildAddonOrderItem(
                                        addon,
                                        addonDate,
                                        occurrence.quantity
                                    );
                                })
                            );
                        }

                        if (isOnlineBookingBoardingItem(item)) {
                            return item.addons.flatMap(addon => {
                                const occurrences = getBoardingAddonOccurrences(item, addon);

                                // No schedule: the add-on keeps its single undated line.
                                if (!occurrences) {
                                    return [buildAddonOrderItem(addon)];
                                }

                                return occurrences.map(occurrence => {
                                    const addonDate = occurrence.date.clone();

                                    if (item.checkInTime) {
                                        addonDate
                                            .set('hour', item.checkInTime.hour)
                                            .set('minute', item.checkInTime.minute);
                                    }

                                    return buildAddonOrderItem(
                                        addon,
                                        addonDate,
                                        occurrence.quantity
                                    );
                                });
                            });
                        }

                        return item.addons.map(addon => {
                            const addonDate = 'date' in item ? item.date?.clone() : undefined;

                            if (isOnlineBookingGroomingItem(item)) {
                                addonDate?.set('hour', item.time?.hour ?? 0);
                                addonDate?.set('minute', item.time?.minute ?? 0);
                            }

                            return buildAddonOrderItem(
                                addon,
                                addonDate,
                                getOnlineBookingAddonUnits(item, addon)
                            );
                        });
                    })
                };
            }

            const requiresApproval = bookingRequiresApproval(
                bookingItems,
                onlineBookingPreferences
            );
            const collectDeposit = getOnlineBookingDepositRequired(state);

            let bookingStatus = BookingStatus.CONFIRMED;

            if (requiresApproval) {
                bookingStatus = BookingStatus.REQUESTED;
            }

            if (collectDeposit) {
                bookingStatus = BookingStatus.PENDING;
            }

            let groomingDate: Moment | undefined = undefined;

            const bookingData: SaveBooking = {
                uuid: uuid(),
                marketplaceId,

                customerId: customerId,

                order: orderData,

                appointments: bookingItems.reduce((acc, item) => {
                    if (!item.service) {
                        return acc;
                    }

                    if (isOnlineBookingSessionItem(item)) {
                        const addresses = customer?.addresses ?? [];
                        const customerAddress =
                            addresses.find(entry => entry.id === item.addressId) ??
                            addresses.find(
                                entry => entry.id === resolveWalkingCustomerAddressId(addresses)
                            );

                        acc.push(
                            buildWalkingSaveAppointment(item, {
                                // The POST echoes this uuid back on every coded 422 error, and it
                                // is the only way to tell which booking item failed.
                                uuid: item.uuid,
                                marketplaceAddressId: addressId,
                                customerId,
                                notes,
                                status: bookingStatus,
                                timeZone,
                                orderItemPrice: getOnlineBookingItemPrice(item),
                                customerAddress
                            })
                        );

                        return acc;
                    }

                    const bookingData: SaveAppointment = {
                        uuid: uuid(),
                        petId: item.pet.id ?? 0,
                        serviceId: item.service?.id ?? 0,
                        addressId: addressId,
                        customerId: customerId,
                        staffId: 'staff' in item && item.staff?.id ? item.staff?.id : undefined,
                        createdBy: 'staff' in item && item.staff?.id ? item.staff?.id : undefined,
                        notes: notes,
                        channel: BookingChannel.ONLINE_BOOKING,
                        status: bookingStatus,
                        orderItemPrice: getOnlineBookingItemPrice(item)
                    };

                    if (isOnlineBookingGroomingItem(item) || isOnlineBookingEvaluationItem(item)) {
                        if (!groomingDate) {
                            groomingDate = item.date?.clone();
                            groomingDate?.set('hour', item.time?.hour ?? 0);
                            groomingDate?.set('minute', item.time?.minute ?? 0);
                        }

                        bookingData.bookedAt = groomingDate?.clone();

                        // If the marketplace has a work schedule,
                        // we need to add the duration of the service to the next appointment date
                        if (scheduleType === MarketplaceScheduleType.Work) {
                            const duration = getServiceDurationByPet(
                                item.service,
                                item.pet.type,
                                item.pet.size
                            );

                            if (duration) {
                                groomingDate = groomingDate?.add(duration, 'minutes');
                            }
                        }
                    }

                    if (isOnlineBookingDaycareItem(item)) {
                        const days = item.days ?? [];

                        if (days.length === 0) {
                            throw new Error('Daycare days not selected');
                        }

                        for (const day of days) {
                            if (day.roomUnitId === undefined) {
                                throw new Error('Daycare room unit not assigned');
                            }

                            const dayAppointment: SaveAppointment = {
                                ...bookingData,
                                uuid: uuid(),
                                roomUnitId: day.roomUnitId,
                                tasks: []
                            };

                            if (item.dropOffTime) {
                                dayAppointment.bookedAt = day.date
                                    .clone()
                                    .set('hour', item.dropOffTime.hour)
                                    .set('minute', item.dropOffTime.minute);
                            }

                            if (item.pickUpTime) {
                                dayAppointment.bookingEndAt = day.date
                                    .clone()
                                    .set('hour', item.pickUpTime.hour)
                                    .set('minute', item.pickUpTime.minute);
                            }

                            for (const feeding of item.feedings ?? []) {
                                for (const momentTypeId of feeding.momentTypes) {
                                    const momentType = masterData.feedingMomentTypes.find(
                                        m => m.id === momentTypeId
                                    );

                                    dayAppointment.tasks?.push({
                                        ...feeding,
                                        uuid: uuid(),
                                        momentTypeId: momentTypeId,
                                        date: day.date
                                            .clone()
                                            .set('hour', momentType?.hour ?? 0)
                                            .set('minute', momentType?.minute ?? 0)
                                    } as FeedingTask);
                                }
                            }

                            for (const medication of item.medications ?? []) {
                                for (const scheduleTime of medication.schedule) {
                                    dayAppointment.tasks?.push({
                                        ...medication,
                                        uuid: uuid(),
                                        date: day.date
                                            .clone()
                                            .set('hour', scheduleTime.hour ?? 0)
                                            .set('minute', scheduleTime.minute ?? 0)
                                    } as MedicationTask);
                                }
                            }

                            acc.push(dayAppointment);
                        }

                        return acc;
                    }

                    if (isOnlineBookingBoardingItem(item)) {
                        if (!item.roomUnitId) {
                            throw new Error('Boarding room unit not assigned');
                        }

                        bookingData.bookedAt = item.startDate?.clone();
                        bookingData.bookingEndAt = item.endDate?.clone();
                        bookingData.roomUnitId = item.roomUnitId;

                        // Apply the shared drop-off / pick-up times the customer
                        // selected in the boarding TimePicker step (CUD-4473).
                        if (item.checkInTime && item.checkOutTime) {
                            bookingData.bookedAt
                                ?.set('hour', item.checkInTime.hour)
                                .set('minute', item.checkInTime.minute);
                            bookingData.bookingEndAt
                                ?.set('hour', item.checkOutTime.hour)
                                .set('minute', item.checkOutTime.minute);

                            bookingData.tasks = [];

                            for (const feeding of item.feedings ?? []) {
                                for (const momentTypeId of feeding.momentTypes) {
                                    const feedingDate = item.startDate?.clone();

                                    while (feedingDate?.isBefore(item.endDate)) {
                                        const momentType = masterData.feedingMomentTypes.find(
                                            m => m.id === momentTypeId
                                        );

                                        bookingData.tasks?.push({
                                            ...feeding,
                                            uuid: uuid(),
                                            momentTypeId: momentTypeId,
                                            date: feedingDate
                                                .clone()
                                                .set('hour', momentType?.hour ?? 0)
                                                .set('minute', momentType?.minute ?? 0)
                                        } as FeedingTask);

                                        feedingDate.add(1, 'day');
                                    }
                                }
                            }

                            for (const medication of item.medications ?? []) {
                                for (const scheduleTime of medication.schedule) {
                                    if (medication.scheduleMode === MedicationScheduleMode.Daily) {
                                        const medicationDate = item.startDate?.clone().tz(timeZone);

                                        while (medicationDate?.isBefore(item.endDate)) {
                                            bookingData.tasks?.push({
                                                ...medication,
                                                uuid: uuid(),
                                                date: medicationDate
                                                    .clone()
                                                    .tz(timeZone)
                                                    .set('hour', scheduleTime.hour ?? 0)
                                                    .set('minute', scheduleTime.minute ?? 0)
                                            } as MedicationTask);

                                            medicationDate.add(1, 'day');
                                        }
                                    }

                                    if (
                                        medication.scheduleMode ===
                                        MedicationScheduleMode.SpecificDays
                                    ) {
                                        // Use moment.tz to ensure the date is in business timezone
                                        // The scheduleTime.date should already have timezone from MedicationForm,
                                        // but we explicitly set it here as a safety measure
                                        const specificDate = scheduleTime.date
                                            ? moment
                                                  .tz(
                                                      scheduleTime.date.format('YYYY-MM-DD'),
                                                      timeZone
                                                  )
                                                  .set('hour', scheduleTime.hour ?? 0)
                                                  .set('minute', scheduleTime.minute ?? 0)
                                            : undefined;

                                        if (specificDate) {
                                            bookingData.tasks?.push({
                                                ...medication,
                                                uuid: uuid(),
                                                date: specificDate
                                            } as MedicationTask);
                                        }
                                    }
                                }
                            }
                        }
                    }

                    acc.push(bookingData);

                    return acc;
                }, [] as Array<SaveAppointment>)
            };

            if (
                bookingItems.some(
                    item =>
                        isOnlineBookingSessionItem(item) &&
                        item.service != null &&
                        resolveWalkingServiceConfig(item.service).inHomeService === true
                )
            ) {
                bookingData.addressId = getOnlineBookingWalkingAddressId(state);
            }

            let booking: Booking | undefined = undefined;

            if (collectDeposit) {
                const depositBookingData: SaveBooking = {
                    ...bookingData,
                    order: bookingData.order ?? {
                        uuid: uuid(),
                        marketplaceId,
                        customerId,
                        issuedAt: today,
                        items: []
                    }
                };

                booking = await dispatch(createBookingForDepositThunk(depositBookingData));
                await dispatch(createDepositInvoiceThunk(createWrapper(state), booking.id));
            } else {
                booking = await dispatch(saveBookingThunk(bookingData));
            }

            await dispatch(applyPendingPromocodeThunk());

            dispatch({
                type: ONLINE_BOOKING_CREATE_BOOKING_SUCCESS
            });

            // Analytics
            // Bookings with deposit are tracked after the deposit is confirmed,
            // see confirmBookingDepositThunk
            if (!collectDeposit) {
                const purchaseItems = toPurchaseItems(bookingItems);

                if (requiresApproval) {
                    trackBookingRequested({
                        transaction_id: bookingData.uuid,
                        marketplace_id: marketplaceId,
                        marketplace_slug: slug,
                        business_area: getBusinessArea(bookingItems),
                        services_count: purchaseItems.length
                    });
                } else {
                    trackBookingCompleted({
                        transaction_id: bookingData.uuid,
                        marketplace_id: marketplaceId,
                        marketplace_slug: slug,
                        business_area: getBusinessArea(bookingItems),
                        deposit_value: 0,
                        items: purchaseItems
                    });
                }
            }

            return booking;
        } catch (error) {
            dispatch({ type: ONLINE_BOOKING_ERROR });

            throw error;
        }
    };
};

export const confirmBookingDepositThunk = (
    invoiceId: number,
    sessionId: string
): ThunkAction<Promise<void>, RootState, null, RootAction> => {
    return async (dispatch, getState) => {
        try {
            dispatch({
                type: ONLINE_BOOKING_CONFIRM_BOOKING_DEPOSIT_START
            });

            const state = getState();

            await dispatch(
                confirmDepositThunk(createWrapper(state), invoiceId, 'credit_card', sessionId)
            );

            dispatch({
                type: ONLINE_BOOKING_CONFIRM_BOOKING_DEPOSIT_SUCCESS
            });

            // Analytics
            const { id: marketplaceId, slug } = state.marketplace.data;
            const { bookingItems } = state.onlineBooking;
            const booking = state.bookings.booking;

            trackBookingCompleted({
                transaction_id: booking?.uuid ?? String(invoiceId),
                marketplace_id: marketplaceId,
                marketplace_slug: slug,
                business_area: getBusinessArea(bookingItems),
                deposit_value: getBookingDepositValue(state),
                items: toPurchaseItems(bookingItems)
            });

            return;
        } catch (error) {
            dispatch({
                type: ONLINE_BOOKING_ERROR
            });

            throw error;
        }
    };
};

/**
 * Fires the GA4 `purchase` conversion for a deposit booking once the deposit
 * payment has succeeded. The card-on-file checkout confirms the payment via
 * `payWithTokenThunk` (not the legacy `confirmBookingDepositThunk`), so the
 * conversion is dispatched from the checkout success handler instead. Reads
 * everything it needs from state — dispatch it right after a successful token
 * payment, before navigating to the Success step.
 */
export const trackDepositBookingCompletedThunk =
    (): ThunkAction<void, RootState, null, RootAction> => (_dispatch, getState) => {
        const state = getState();
        const { id: marketplaceId, slug } = state.marketplace.data;
        const { bookingItems } = state.onlineBooking;
        const booking = state.bookings.booking;
        const depositInvoice = state.invoices.depositInvoice;

        trackBookingCompleted({
            transaction_id: booking?.uuid ?? String(depositInvoice?.id ?? ''),
            marketplace_id: marketplaceId,
            marketplace_slug: slug,
            business_area: getBusinessArea(bookingItems),
            deposit_value: getBookingDepositValue(state),
            items: toPurchaseItems(bookingItems)
        });
    };
